import type { MissingInformationUpdates } from './missingInformation'

export type WritebackStatus = {
  authenticated: boolean
  configured: boolean
}

export type MissingInformationUpdateRequest = {
  spreadsheetId: string
  organizationRow: number | null
  opportunityRow: number | null
  updates: MissingInformationUpdates
}

export type MissingInformationUpdateResult = {
  updatedFields: string[]
  undoToken: string
}

export class WritebackError extends Error {
  code: string
  conflicts: string[]

  constructor(
    message: string,
    code = 'WRITEBACK_FAILED',
    conflicts: string[] = [],
  ) {
    super(message)
    this.code = code
    this.conflicts = conflicts
  }
}

async function readJson(
  response: Response,
): Promise<Record<string, unknown>> {
  const contentType = response.headers.get('content-type') ?? ''

  if (!contentType.includes('application/json')) {
    throw new Error(
      'The secure write-back endpoint is unavailable.',
    )
  }

  return response.json() as Promise<Record<string, unknown>>
}

function responseError(
  body: Record<string, unknown>,
  fallback: string,
): WritebackError {
  return new WritebackError(
    typeof body.message === 'string'
      ? body.message
      : fallback,
    typeof body.code === 'string'
      ? body.code
      : 'WRITEBACK_FAILED',
    Array.isArray(body.conflicts)
      ? body.conflicts.filter(
          (field): field is string =>
            typeof field === 'string',
        )
      : [],
  )
}

export async function getWritebackStatus(): Promise<WritebackStatus> {
  try {
    const response = await fetch('/api/crm-update', {
      credentials: 'include',
      headers: {
        accept: 'application/json',
      },
    })

    const body = await readJson(response)

    return {
      configured: body.configured === true,
      authenticated: body.authenticated === true,
    }
  } catch {
    return {
      configured: false,
      authenticated: false,
    }
  }
}

async function performMissingInformationUpdate(
  request: MissingInformationUpdateRequest,
): Promise<MissingInformationUpdateResult> {
  const response = await fetch('/api/crm-update', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify(request),
  })

  const body = await readJson(response)

  if (!response.ok) {
    throw responseError(
      body,
      'The spreadsheet update could not be completed.',
    )
  }

  return {
    updatedFields: Array.isArray(body.updatedFields)
      ? body.updatedFields.filter(
          (field): field is string =>
            typeof field === 'string',
        )
      : [],
    undoToken:
      typeof body.undoToken === 'string'
        ? body.undoToken
        : '',
  }
}

/* Keeps the old API working for the general sidebar review modal. */
export async function submitMissingInformationUpdate(
  request: MissingInformationUpdateRequest,
): Promise<string[]> {
  const result = await performMissingInformationUpdate(request)
  return result.updatedFields
}

/* Dashboard Take Action uses this because it needs Undo. */
export async function submitMissingInformationUpdateWithUndo(
  request: MissingInformationUpdateRequest,
): Promise<MissingInformationUpdateResult> {
  const result = await performMissingInformationUpdate(request)

  if (!result.undoToken) {
    throw new WritebackError(
      'The update succeeded but an Undo token was not returned.',
      'UNDO_TOKEN_MISSING',
    )
  }

  return result
}

export async function submitMissingInformationUndo(
  undoToken: string,
): Promise<string[]> {
  const response = await fetch('/api/crm-undo', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({ undoToken }),
  })

  const body = await readJson(response)

  if (!response.ok) {
    throw responseError(
      body,
      'The spreadsheet update could not be undone.',
    )
  }

  return Array.isArray(body.undoneFields)
    ? body.undoneFields.filter(
        (field): field is string =>
          typeof field === 'string',
      )
    : []
}
